/**
 * <section-entrance-reveal>
 * Adds a reversible viewport state for section-owned entrance transitions.
 *
 * Dispatches `theme-entrance-reveal:change` when visibility changes.
 * By default, `detail.isVisible` is true when at least 15% of the reveal root
 * is on screen. Set `data-reveal-at-center` to wait until the reveal root
 * crosses the viewport center line.
 */
if (!customElements.get('section-entrance-reveal')) {
  class SectionEntranceReveal extends HTMLElement {
    connectedCallback() {
      if (this.isBound) return;
      this.isBound = true;

      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
        || !('IntersectionObserver' in window)
      ) {
        this.setRevealVisible(true);
        return;
      }

      const revealAtCenter = this.hasAttribute('data-reveal-at-center');
      const observerOptions = revealAtCenter
        ? { rootMargin: '0px 0px -50% 0px', threshold: 0 }
        : { threshold: 0.15 };

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.setRevealVisible(entry?.isIntersecting ?? false);
        },
        observerOptions,
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.isBound = false;
    }

    /**
     * @param {boolean} isVisible
     */
    setRevealVisible(isVisible) {
      const nextVisible = Boolean(isVisible);
      const wasVisible = this.classList.contains('is-visible');
      this.classList.toggle('is-visible', nextVisible);
      if (wasVisible === nextVisible) return;

      this.dispatchEvent(new CustomEvent('theme-entrance-reveal:change', {
        bubbles: true,
        detail: { isVisible: nextVisible },
      }));
    }
  }

  customElements.define('section-entrance-reveal', SectionEntranceReveal);
}
